import type { Lead } from '@/types';
import i18n from '@/i18n/config';

import React from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Textarea } from '@heroui/react';

interface AddNoteModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead | null;
  isSubmitting: boolean;
  onSubmit: (note: string) => void;
}

export function AddNoteModal({ isOpen, onOpenChange, lead, isSubmitting, onSubmit }: AddNoteModalProps) {
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    if (!isOpen) {
      setNote('');
    }
  }, [isOpen]);

  const handleSubmit = React.useCallback(() => {
    if (!note.trim()) {
      return;
    }

    onSubmit(note.trim());
    setNote('');
  }, [note, onSubmit]);

  const handleKeyPress = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  if (!lead) return null;

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={onOpenChange} size="md">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Add Note to {lead.domain}</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4" onKeyDown={handleKeyPress}>
                <Textarea
                  label={i18n.t('copy.note')}
                  placeholder={i18n.t('copy.enter_your_note_here')}
                  value={note}
                  onValueChange={setNote}
                  minRows={4}
                  isRequired
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="danger" variant="light" onPress={onClose} isDisabled={isSubmitting}>{i18n.t('modals.exportConfirm.cancel')}</Button>
              <Button color="primary" onPress={handleSubmit} isLoading={isSubmitting} isDisabled={!note.trim()}>
                Add Note
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

